import { useMemo } from 'react';

import { useAuth } from './useAuth';

/**
 * The plugin ids the active company is entitled to.
 *
 * `effectivePlugins` is the backend-computed, partnership-resolved set — the
 * same one App.tsx loads plugin bundles from — and is preferred over the raw
 * `enabledPlugins`. The fallback exists only for API responses that predate the
 * field; it is not a second policy.
 *
 * This is the *entitlement* half of a visibility decision. It answers "has this
 * company bought the module", never "may this user see it" — permissions answer
 * that, and both must hold. A user can carry a permission from a role while the
 * company has the module switched off (roles are seeded from the whole access
 * catalog, not from the company's plugin list), which is exactly how surfaces
 * for un-purchased modules used to leak onto the dashboard.
 */
export const useEntitledPlugins = (): {
  plugins: ReadonlySet<string>;
  hasPlugin: (pluginId: string) => boolean;
} => {
  const { company } = useAuth();

  return useMemo(() => {
    const list = company?.effectivePlugins?.length
      ? company.effectivePlugins
      : (company?.enabledPlugins ?? []);
    const plugins = new Set(list);
    return { plugins, hasPlugin: (pluginId: string) => plugins.has(pluginId) };
  }, [company?.effectivePlugins, company?.enabledPlugins]);
};
